import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pTradeSellPendingRelease.css';

const mockOrderData = {
  orderId: '4162539730316465',
  buyer: {
    name: 'Ubong Ben',
    initial: 'U',
    verified: true,
    fullName: 'UBONG BEN'
  },
  timeRemaining: 268, // seconds (04:28)
  orderDetails: {
    quantity: '1.00',
    crypto: 'USDT',
    amountFiat: '1430.00',
    currency: 'NGN',
    price: '1430.00',
    priceUnit: 'NGN/USDT'
  },
  hasNewMessage: true
};

const P2pTradeSellPendingRelease: React.FC = () => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(mockOrderData.timeRemaining);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (timeRemaining / mockOrderData.timeRemaining) * 100;

  const handleBack = () => {
    navigate(-1);
  };

  const handleChat = () => {
    console.log('Opening chat...');
  };

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(mockOrderData.orderId);
  };

  const handleReleaseCrypto = () => {
    console.log('Releasing crypto...');
  };

  const handleDispute = () => {
    console.log('Dispute opened');
  };

  return (
    <div className="pending-release-container">
      <header className="pending-release-header">
        <button className="pending-release-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <h1 className="pending-release-title">Sell USDT</h1>

        <button className="pending-release-chat-button" onClick={handleChat} aria-label="Chat">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17 8.5C17 12.366 13.866 15.5 10 15.5C8.95 15.5 7.951 15.28 7.049 14.888L3 16L4.112 11.951C3.72 11.049 3.5 10.05 3.5 9C3.5 5.134 6.634 2 10.5 2C14.366 2 17.5 5.134 17.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {mockOrderData.hasNewMessage && (
            <span className="pending-release-chat-badge">1</span>
          )}
        </button>
      </header>

      <div className="pending-release-progress-container">
        <div className="pending-release-progress-bar">
          <div 
            className="pending-release-progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="pending-release-content">
        <div className="pending-release-merchant-card">
          <div className="pending-release-merchant-avatar">
            {mockOrderData.buyer.initial}
          </div>
          
          <div className="pending-release-merchant-info">
            <div className="pending-release-merchant-name">
              {mockOrderData.buyer.name}
              {mockOrderData.buyer.verified && (
                <svg className="pending-release-verified-badge" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 0L11.5 6.5L18 8L11.5 9.5L10 16L8.5 9.5L2 8L8.5 6.5L10 0Z" fill="#22c35d"/>
                </svg>
              )}
            </div>
            <div className="pending-release-merchant-label">Merchant</div>
          </div>

          <h2 className="pending-release-order-heading">Verifying Payment</h2>
          
          <p className="pending-release-timer-label">
            Please wait while the payment is being verified.
          </p>

          <div className="pending-release-timer-card">
            <div className="pending-release-timer-display">{formatTime(timeRemaining)}</div>
          </div>
        </div>

        <div className="pending-release-order-id-card">
          <div className="pending-release-order-id-label">ID</div>
          <div className="pending-release-order-id-value">{mockOrderData.orderId}</div>
          <button 
            className="pending-release-copy-button" 
            onClick={handleCopyOrderId}
            aria-label="Copy order ID"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="pending-release-details-card">
          <div className="pending-release-detail-row">
            <span className="pending-release-detail-label">Quantity</span>
            <div className="pending-release-detail-value-group">
              <span className="pending-release-detail-amount">{mockOrderData.orderDetails.quantity}</span>
              <span className="pending-release-detail-crypto">{mockOrderData.orderDetails.crypto}</span>
            </div>
          </div>

          <div className="pending-release-divider" />

          <div className="pending-release-detail-row">
            <span className="pending-release-detail-label">Amount (Fiat)</span>
            <div className="pending-release-detail-value-group">
              <span className="pending-release-detail-amount">{mockOrderData.orderDetails.amountFiat}</span>
              <span className="pending-release-detail-currency">{mockOrderData.orderDetails.currency}</span>
            </div>
          </div>

          <div className="pending-release-divider" />

          <div className="pending-release-detail-row">
            <span className="pending-release-detail-label">Price</span>
            <div className="pending-release-detail-value-group">
              <span className="pending-release-detail-amount">{mockOrderData.orderDetails.price}</span>
              <span className="pending-release-detail-unit">{mockOrderData.orderDetails.priceUnit}</span>
            </div>
          </div>
        </div>

        <div className="pending-release-merchant-name-card">
          <span className="pending-release-merchant-name-label">Merchant Name</span>
          <span className="pending-release-merchant-name-value">{mockOrderData.buyer.fullName}</span>
        </div>
      </div>

      <div className="pending-release-action-bar">
        <button className="pending-release-primary-button" onClick={handleReleaseCrypto}>
          Release Crypto
        </button>
        
        <button className="pending-release-dispute-button" onClick={handleDispute}>
          Dispute
        </button>
      </div>
    </div>
  );
};

export default P2pTradeSellPendingRelease;